import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getArticles } from '../api/strapi';
import '../styles/RecentPosts.css';

// Arrow SVGs
import ArrowLeft from '../images/portfolio/Arrow-Left.svg';
import ArrowRight from '../images/portfolio/Arrow-Right.svg';

const STRAPI_BASE_URL = 'http://localhost:1337';

const fallbackArticles = [
  {
    id: 1,
    title: 'Integrating AI Into Product Design Workflows as a UX Engineer',
    slug: 'integrating-ai-into-product-design-workflows-as-a-ux-engineer',
    description: 'Exploring practical methods for integrating generative AI tools into rapid prototyping and component architecture.',
    coverUrl: '',
    bgColor: '#eaf4f8'
  },
  {
    id: 2,
    title: 'UX Design in a World of AI: Why Empathy and Trust Matter More Than Ever',
    slug: 'ux-design-in-a-world-of-ai-why-empathy-and-trust-matter-more-than-ever',
    description: 'Why human-centered research and ethical design patterns are critical as algorithmic interfaces scale.',
    coverUrl: '',
    bgColor: '#fde8ef'
  },
  {
    id: 3,
    title: 'What Traditional Art Taught Me About Designing Better Digital Products',
    slug: 'what-traditional-art-taught-me-about-designing-better-digital-products',
    description: 'Translating classical composition techniques, spatial balance, and color harmony into digital interfaces.',
    coverUrl: '',
    bgColor: '#eaf6ec'
  },
  {
    id: 4,
    title: 'Building Scaleable Design Tokens for Multi-Brand Enterprise Ecosystems',
    slug: 'building-scaleable-design-tokens',
    description: 'How to structure semantic design tokens that sync seamlessly between Figma and React component libraries.',
    coverUrl: '',
    bgColor: '#fff1e6'
  },
  {
    id: 5,
    title: 'Cognitive Load Reduction in Industrial & Telemetry Control Planes',
    slug: 'cognitive-load-reduction-industrial-telemetry',
    description: 'Designing high-contrast telemetry dashboards and alarm filtering systems for mission-critical operations.',
    coverUrl: '',
    bgColor: '#f4eaf8'
  },
  {
    id: 6,
    title: 'Micro-Interactions that Delight: Enhancing E-Commerce Subscription Flows',
    slug: 'micro-interactions-subscription-flows',
    description: 'Refining onboarding progress trackers and interactive customization widgets to drive long-term user retention.',
    coverUrl: '',
    bgColor: '#e6f5f3'
  }
];

export default function RecentPosts() {
  const [articles, setArticles] = useState(fallbackArticles);
  const [displayIndex, setDisplayIndex] = useState(6);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const carouselRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    getArticles()
      .then((data) => {
        if (data && data.length > 0) {
          const formatted = data.map((item, idx) => {
            let coverUrl = '';
            if (item.cover && item.cover.url) {
              coverUrl = `${STRAPI_BASE_URL}${item.cover.url}`;
            }
            const defaultBg = fallbackArticles[idx % fallbackArticles.length].bgColor;
            return {
              id: item.id || (idx + 1),
              title: item.title,
              slug: item.slug || `article-${item.id}`,
              description: item.description || '',
              coverUrl,
              bgColor: defaultBg
            };
          });
          setArticles(formatted);
          setDisplayIndex(formatted.length);
        }
      })
      .catch((err) => {
        console.warn('RecentPosts: using fallback articles data:', err);
      });
  }, []);

  const totalOriginals = articles.length;
  const displayItems = [...articles, ...articles, ...articles];
  const itemWidth = 512; // 480px card width + 32px gap

  const handleScrollCarousel = (direction) => {
    if (totalOriginals === 0) return;
    setIsTransitioning(true);
    if (direction === 'right') {
      setDisplayIndex((prev) => prev + 1);
    } else {
      setDisplayIndex((prev) => prev - 1);
    }
  };

  const handleTransitionEnd = () => {
    if (displayIndex >= totalOriginals * 2) {
      const resetIndex = (displayIndex % totalOriginals) + totalOriginals;
      setIsTransitioning(false);
      setDisplayIndex(resetIndex);
    } else if (displayIndex < totalOriginals) {
      const resetIndex = (displayIndex % totalOriginals) + totalOriginals;
      setIsTransitioning(false);
      setDisplayIndex(resetIndex);
    }
  };

  const currentRealIndex = totalOriginals > 0 
    ? ((displayIndex % totalOriginals + totalOriginals) % totalOriginals) 
    : 0;

  return (
    <section className="recent-posts-section py-5 position-relative">
      <div className="container">
        {/* Header Block */}
        <div className="recent-posts-header text-start mb-5">
          <h1 className="recent-posts-title mb-3">Recent Thoughts</h1>
          <p className="recent-posts-subtitle">
            Welcome to my corner of the internet. These posts are where I share what I'm learning, building, and creating. From UX and front-end engineering to art and the occasional creative rabbit hole. If you're curious about the people, process, and thinking behind the work, you're in the right place.
          </p>
        </div>

        {/* Controls Row */}
        <div className="recent-posts-controls d-flex align-items-center gap-3 mb-4">
          <span className="posts-page-indicator m-0">
            Posts <span className="posts-page-fraction">{currentRealIndex + 1}/{totalOriginals}</span>
          </span>
          <div className="d-flex gap-2">
            <button
              className="posts-arrow-btn"
              onClick={() => handleScrollCarousel('left')}
              aria-label="Previous Post"
            >
              <img src={ArrowLeft} alt="Previous" />
            </button>
            <button
              className="posts-arrow-btn"
              onClick={() => handleScrollCarousel('right')}
              aria-label="Next Post"
            >
              <img src={ArrowRight} alt="Next" />
            </button>
          </div>
        </div>
      </div>

      {/* Carousel Track Outer Wrapper */}
      <div className="recent-posts-carousel-wrapper">
        <div
          ref={carouselRef}
          className="recent-posts-carousel-track"
          style={{
            transform: `translateX(-${displayIndex * itemWidth}px)`,
            transition: isTransitioning ? 'transform 0.45s cubic-bezier(0.25, 1, 0.5, 1)' : 'none',
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {displayItems.map((item, index) => {
            return (
              <div
                key={`${item.id || index}-${index}`}
                className="recent-post-item flex-shrink-0"
                onClick={() => navigate(`/recent-thoughts/${item.slug}`)}
                style={{ cursor: 'pointer' }}
              >
                <div 
                  className="recent-post-card"
                  style={{ backgroundColor: item.bgColor }}
                >
                  <div className="recent-post-card-content p-4 p-md-5 d-flex flex-column h-100 text-start w-100">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="recent-post-badge">ARTICLE</span>
                      <span className="recent-post-readtime">5 min read</span>
                    </div>
                    <h3 className="recent-post-card-title mb-3">{item.title}</h3>
                    <p className="recent-post-card-desc mb-4 flex-grow-1">{item.description}</p>
                    <div className="recent-post-card-link fw-bold text-dark d-flex align-items-center gap-2 mt-auto">
                      <span>Read Article</span>
                      <span className="arrow-icon">→</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
